import {getDistrict} from "../../utils/SchoolBlocksUtilities";
import {useContext, useEffect, useRef, useState} from "react";
import {StoreContext} from "../../stores/StoreLoader";
import styles from './styles/WebAppInstallPrompt.module.scss';
import FontAwesome from "../utilities/FontAwesome";
import ClickableLink from "../utilities/ClickableLink";
import classNames from 'classnames';
import {getCookieFromString, setCookie} from "../../utils/StringUtilities";
import {useAppAllowed} from "../../hooks/useAppAllowed";
import WatsonApi from "../../backends/WatsonApi";
import NotificationManager from "../notifications/NotificationManager";
import {askPermission, isPushSupported} from "../../pages/UserSettings/NotificationSettings";

const cookieKey = "webapp";

export function WebAppInstallPrompt(props: {
    user?: IUser
}) {
    const {organizationStore, interfaceStore, modalStore, userStore} = useContext(StoreContext);

    // iOS doesn't support web app downloads, but if it's Safari we can instruct how to add to homescreen
    const isIosSafari = /(iPod|iPhone)/.exec(interfaceStore.userAgent) && /Safari/.exec(interfaceStore.userAgent);

    const [showAlert, setShowAlert] = useState(false);
    const [showPushMessage, setShowPushMessage] = useState(false);
    const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

    function handleInstallPrompt(e) {
        deferredPrompt.current = e;
        if (
            interfaceStore.breakpoint === "break-point-xs" &&
            !(
                window.matchMedia('(display-mode: minimal-ui)').matches ||
                window.matchMedia('(display-mode: standalone)').matches // maybe when added to homescreen on iOS?
            ) &&
            !getCookieFromString(cookieKey, document.cookie)
        ) {
            // only show prompt on small screens
            setShowAlert(true);
        }
    }

    function handleInstalled() {
        setShowAlert(false);
    }

    function enablePush() {
        setShowPushMessage(false);

        if (isPushSupported() && props.user) {
            navigator.serviceWorker.ready
                .then(function (registration) {
                    askPermission()
                        .then(function () {

                            return registration.pushManager.subscribe({
                                userVisibleOnly: true,
                                applicationServerKey: "BFcZZ6FiITumPysCi_t9B1TANBHATH7r0Y4bKVRomTVRo14k5eNU0L9LhgZ3ogL7xPkGYh-RqtFN14RlnU3rDWg",
                            })

                        })
                        .then(async function (pushSubscription) {
                            console.log("Received PushSubscription: ", JSON.stringify(pushSubscription));
                            const client = await WatsonApi();
                            await client.apis.users.users_push_subscription_create({
                                user_pk: userStore.id,
                                data: {
                                    organization_id:organizationStore.currentOrganization.id,
                                    user_id: userStore.id,
                                    subscription_object: pushSubscription,
                                    user_agent: interfaceStore.userAgent,
                                },
                            })

                            await client.apis.organizations.organizations_users_partial_update({
                                organization_pk: organizationStore.currentOrganization.id,
                                id: (props.user as IUser).id,
                                data: {
                                    notifications: {
                                        phone: (props.user as IUser).notifications?.phone,
                                        email: (props.user as IUser).notifications?.email,
                                        push: true,
                                    },
                                },
                            })
                            NotificationManager.success("Settings have been updated!");
                        })
                        .catch(function (subscriptionError) {
                            NotificationManager.error(subscriptionError.message);
                        })
                })
        }
    }

    useEffect(() => {
        if (!!isIosSafari && !getCookieFromString(cookieKey, document.cookie)) {
            setShowAlert(true);
        }

        window.addEventListener("beforeinstallprompt", handleInstallPrompt);
        window.addEventListener("appinstalled", handleInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
            window.removeEventListener("appinstalled", handleInstalled);
        }
    }, []);

    useEffect(() => {
        if (
            !isPushSupported() ||
            !navigator?.serviceWorker ||
            !("ready" in navigator.serviceWorker)
        ) {
            return;
        }

        navigator.serviceWorker.ready
            .then(() => {
                if (
                    interfaceStore.isPWA &&
                    props.user &&
                    !props.user.notifications?.push &&
                    process.env.BETA_FEATURES_ENABLED
                ) {
                    setShowPushMessage(true);
                }
            })
    }, [])

    const appAllowed = useAppAllowed();

    let installButton, title;
    if (appAllowed) {
        title = organizationStore.organization.title;

        if (isIosSafari) {
            installButton = <button onClick={() => modalStore.addModal({
                type: "iosAppInstallHelp"
            })}>Install</button>
        } else {
            installButton = <button onClick={async () => await deferredPrompt?.current?.prompt()}>Install</button>;
        }
    } else {
        const district = getDistrict(organizationStore.organization);
        title = district.title;
        installButton =
            <ClickableLink href={district.primary_hostname.scheme + "://" + district.primary_hostname.hostname}>Visit
                District to Install</ClickableLink>
    }

    const containerClassName = classNames({
        [styles.container]: true,
        [styles.containerCanInstall]: showAlert && organizationStore.organization.type === "district",
        [styles.containerPush]: showPushMessage && appAllowed,
    })

    return <div>
        {(showAlert && !interfaceStore.isPWA) && <div className={containerClassName}>
            <div>
                <button onClick={() => {
                    setShowAlert(false);
                    // since on iOS Safari we seemingly can't determine if inside the app or not, we just want to hide it for good
                    setCookie(cookieKey, "ignore", isIosSafari ? 999999 : 3);
                }}>
                    <FontAwesome name={"fa-times"} size={"2x"} prefix={"fas"}/>
                </button>
            </div>
            <div>
                <h2>{title}</h2>
                <span>Get our free app. It won't take up space on your phone.</span>
            </div>
            <div>
                {installButton}
            </div>
        </div>}
        { (showPushMessage && appAllowed && userStore.id) &&
            <div className={containerClassName}>
                <div>
                    <h2>{title}</h2>
                    <span>Enable Push Notifications</span>
                </div>
                <div>
                    <button onClick={enablePush}>
                        Enable
                    </button>
                </div>
            </div>}
    </div>
}
